import { getActiveLocale, t } from '../i18n/translate';

export interface ReadTimeEstimate {
  wordCount: number;
  readMinMinutes: number;
  readMaxMinutes: number;
  speakMinMinutes: number;
  speakMaxMinutes: number;
}

/** Fastest comfortable aloud pace (words per minute) */
export const SPEAK_WPM_FAST = 225;

/** Slowest comfortable aloud pace (words per minute) */
export const SPEAK_WPM_SLOW = 140;

export function countWords(text: string): number {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).filter(Boolean).length;
}

/** Silent reading range ~150–300 wpm */
export function estimateReadTimeRange(wordCount: number): { min: number; max: number } {
  if (wordCount <= 0) return { min: 0, max: 0 };
  return {
    min: Math.max(1, Math.ceil(wordCount / 300)),
    max: Math.max(1, Math.ceil(wordCount / 150)),
  };
}

/** Speaking / presenting ~140–225 wpm */
export function estimateSpeakingTimeRange(wordCount: number): { min: number; max: number } {
  if (wordCount <= 0) return { min: 0, max: 0 };
  return {
    min: Math.max(1, Math.ceil(wordCount / SPEAK_WPM_FAST)),
    max: Math.max(1, Math.ceil(wordCount / SPEAK_WPM_SLOW)),
  };
}

export function estimateReadTime(text: string): ReadTimeEstimate {
  const wordCount = countWords(text);
  const read = estimateReadTimeRange(wordCount);
  const speak = estimateSpeakingTimeRange(wordCount);
  return {
    wordCount,
    readMinMinutes: read.min,
    readMaxMinutes: read.max,
    speakMinMinutes: speak.min,
    speakMaxMinutes: speak.max,
  };
}

export function formatAloudDurationLabel(wordCount: number): string {
  if (wordCount <= 0) return '';
  const { min, max } = estimateSpeakingTimeRange(wordCount);
  if (min === max) {
    return t(min === 1 ? 'step.1.length.aloud_one' : 'step.1.length.aloud_many', { min });
  }
  return t('step.1.length.aloud_range', { min, max });
}

export function formatWordCountLabel(wordCount: number): string {
  return t(wordCount === 1 ? 'step.1.length.word_one' : 'step.1.length.word_many', {
    count: wordCount.toLocaleString(getActiveLocale()),
  });
}

export function formatStoryLengthLabel(estimate: ReadTimeEstimate): string {
  if (estimate.wordCount === 0) return '';
  return t('step.1.length.summary', {
    words: formatWordCountLabel(estimate.wordCount),
    aloud: formatAloudDurationLabel(estimate.wordCount),
  });
}

/** @deprecated Prefer formatStoryLengthLabel in the First Step writing workspace dialog */
export function formatSpeakingTimeLabel(estimate: ReadTimeEstimate): string {
  if (estimate.wordCount === 0) return '';
  if (estimate.speakMinMinutes === estimate.speakMaxMinutes) {
    return `~${estimate.speakMinMinutes} min aloud · ${estimate.wordCount.toLocaleString()} words`;
  }
  return `~${estimate.speakMinMinutes}–${estimate.speakMaxMinutes} min aloud · ${estimate.wordCount.toLocaleString()} words`;
}
